/**
 * Social provider visibility config.
 *
 * Each provider has a build-time toggle (`VITE_SSO_<PROVIDER>_ENABLED`). The
 * value is read once at module load and exposed as `enabledProviders` /
 * `isProviderEnabled(p)`. Login/Register/Account-linking views render the
 * button only when the flag is truthy.
 *
 * Truthy = the literal string `"true"`. Anything else (including empty,
 * missing, `"false"`, `"0"`) hides the button.
 *
 * Per-tier `.env*` files own the matrix. Example:
 *   .env.local           → VITE_SSO_KEYCLOAK_ENABLED=true       (only keycloak)
 *   .env.test            → VITE_SSO_GOOGLE_ENABLED=true         (only google)
 *   .env.android.test    → VITE_SSO_GOOGLE_ENABLED=true
 *
 * Enabling a provider here ONLY controls UI visibility. Wiring the actual
 * flow (PKCE init, callback host, native-plugin init for Google/Apple, etc.)
 * still requires the provider-specific env vars (e.g. VITE_GOOGLE_WEB_CLIENT_ID
 * for native Google). Boot-time validation lives in src/boot/social-login.ts.
 */

export type SocialProvider = 'google' | 'microsoft' | 'keycloak' | 'apple';

const PROVIDERS: readonly SocialProvider[] = ['keycloak', 'google', 'microsoft', 'apple'];

const isTruthy = (value: string | boolean | undefined): boolean => String(value) === 'true';

// Each access MUST be a static `import.meta.env.X` so Vite's define replaces it
// with the build-time literal. A computed lookup (`import.meta.env[key]`) gets
// rewritten to `{}[key]` → always undefined → every provider always hidden.
const enabledMap: Record<SocialProvider, boolean> = {
  keycloak: isTruthy(import.meta.env.VITE_SSO_KEYCLOAK_ENABLED),
  google: isTruthy(import.meta.env.VITE_SSO_GOOGLE_ENABLED),
  microsoft: isTruthy(import.meta.env.VITE_SSO_MICROSOFT_ENABLED),
  apple: isTruthy(import.meta.env.VITE_SSO_APPLE_ENABLED),
};

export const enabledProviders: readonly SocialProvider[] = PROVIDERS.filter(
  (p) => enabledMap[p],
);

export const isProviderEnabled = (provider: SocialProvider): boolean => enabledMap[provider];

export const hasAnyProviderEnabled = (): boolean => enabledProviders.length > 0;
